<template>
  <v-card class="mx-auto mb-4" outlined>
    <v-card-title>{{ $t("billing-periods.cost-centres") }}</v-card-title>
    <v-spacer></v-spacer>

    <v-row class="mx-auto">
      <v-col cols="12" sm="4">
        <v-text-field
          :label="$t('billing-period-cost-centres.filter-label')"
          v-model="filter.text"
          @keyup="debouncedUpdateFilteredPivots"
          single-line
          hide-details
        ></v-text-field>
      </v-col>

      <v-col cols="12" sm="3">
        <v-checkbox
          :label="$t('billing-period-cost-centres.is-provider')"
          :input-value="filter.is_provider"
          :indeterminate="filter.is_provider === undefined"
          @click.native.prevent.stop.capture="
            filter.is_provider = cycleTrueFalseUndefined(filter.is_provider);
            debouncedUpdateFilteredPivots();
          "
        ></v-checkbox>
      </v-col>

      <v-col cols="12" sm="3">
        <v-checkbox
          :label="$t('billing-period-cost-centres.is-consumer')"
          :input-value="filter.is_consumer"
          :indeterminate="filter.is_consumer === undefined"
          @click.native.prevent.stop.capture="
            filter.is_consumer = cycleTrueFalseUndefined(filter.is_consumer);
            debouncedUpdateFilteredPivots();
          "
        ></v-checkbox>
      </v-col>

      <v-col cols="12" sm="2">
        <v-col cols="3" sm="1" class="reset">
          <v-btn block v-on:click="resetFilter()">
            <v-icon>
              mdi-filter-remove-outline
            </v-icon>
          </v-btn>
        </v-col>
      </v-col>
    </v-row>

    <v-spacer></v-spacer>

    <v-simple-table dense fixed-header height="600px" class="pivot-table">
      <template v-slot:default>
        <thead>
          <tr>
            <th colspan="2">
              <div>
                {{ $t("billing-period-cost-centres.cost-centre") }}
              </div>
              <div>
                <v-menu offset-y>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn color="primary" text v-bind="attrs" v-on="on">
                      <v-icon class="mr-2">
                        mdi-arrow-down-left
                      </v-icon>
                      {{ selectChangeButtonText }}
                    </v-btn>
                  </template>
                  <v-list>
                    <v-list-item
                      link
                      v-for="(action, key) in modifySelected"
                      :key="key"
                      @click="action()"
                    >
                      <v-list-item-title>{{
                        $t(
                          "billing-period-cost-centres.selected-modify-action." +
                            key
                        )
                      }}</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </div>
            </th>
            <th>
              <div>
                {{ $t("billing-period-cost-centres.as-provider") }}
              </div>
              <div>
                <v-checkbox
                  dense
                  hide-details
                  :input-value="massSet.as_provider"
                  :indeterminate="massSet.as_provider === undefined"
                  @click.native.prevent.stop.capture="
                    massSet.as_provider = cycleTrueFalseUndefined(
                      massSet.as_provider
                    )
                  "
                ></v-checkbox>
              </div>
            </th>
            <th>
              <div>
                {{ $t("billing-period-cost-centres.as-consumer") }}
              </div>
              <div>
                <v-checkbox
                  dense
                  hide-details
                  :input-value="massSet.as_consumer"
                  :indeterminate="massSet.as_consumer === undefined"
                  @click.native.prevent.stop.capture="
                    massSet.as_consumer = cycleTrueFalseUndefined(
                      massSet.as_consumer
                    )
                  "
                ></v-checkbox>
              </div>
            </th>
            <th>
              <div>
                {{ $t("billing-period-cost-centres.on-account") }}
              </div>
              <div>
                <v-text-field
                  hide-details
                  dense
                  reverse
                  v-model="massSet.payment_on_account"
                ></v-text-field>
              </div>
            </th>
            <th>
              <div></div>
              <div>
                <v-btn color="primary" text @click="setSelected()">
                  <v-icon>
                    mdi-table-arrow-down
                  </v-icon>
                </v-btn>
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="pivot in filteredPivots" :key="pivot.id">
            <td>
              <v-checkbox
                class="mt-0"
                hide-details
                dense
                v-model="pivot.is_selected"
              ></v-checkbox>
            </td>
            <td>{{ pivot.name }}</td>
            <td>
              <v-checkbox
                class="mt-0"
                hide-details
                dense
                v-model="pivot.as_provider"
              ></v-checkbox>
            </td>
            <td>
              <v-checkbox
                class="mt-0"
                hide-details
                dense
                v-model="pivot.as_consumer"
              ></v-checkbox>
            </td>
            <td>
              <v-text-field
                hide-details
                dense
                reverse
                v-model="pivot.payment_on_account"
              ></v-text-field>
            </td>
          </tr>
        </tbody>
      </template>
    </v-simple-table>
  </v-card>
</template>

<script>
import { debounce } from "lodash";

export default {
  name: "FormBillingPeriodCostCentres",
  props: {
    costCentrePivots: Array
  },
  data: function() {
    return {
      filter: {
        text: "",
        is_provider: undefined,
        is_consumer: undefined
      },
      massSet: {
        as_provider: undefined,
        as_consumer: undefined,
        payment_on_account: ""
      },
      filteredPivots: {}
    };
  },
  computed: {
    selectChangeButtonText() {
      const length = this.selectedPivots.length;
      return length > 0
        ? this.$t("billing-period-cost-centres.selected") + ": " + length
        : this.$t("billing-period-cost-centres.select");
    },
    selectedPivots() {
      return this.costCentrePivots.filter(pivot => pivot.is_selected);
    },
    modifySelected() {
      return {
        visibleSelect: () => {
          this.filteredPivots.forEach(pivot => (pivot.is_selected = true));
        },
        visibleUnselect: () => {
          this.filteredPivots.forEach(pivot => (pivot.is_selected = false));
        },
        visibleInvert: () => {
          this.filteredPivots.forEach(
            pivot => (pivot.is_selected = !pivot.is_selected)
          );
        },
        allSelect: () => {
          this.costCentrePivots.forEach(pivot => (pivot.is_selected = true));
        },
        allUnselect: () => {
          this.costCentrePivots.forEach(pivot => (pivot.is_selected = false));
        },
        allInvert: () => {
          this.costCentrePivots.forEach(
            pivot => (pivot.is_selected = !pivot.is_selected)
          );
        }
      };
    }
  },
  methods: {
    debouncedUpdateFilteredPivots: function() {
      return debounce(this.updateFilteredPivots, 500)();
    },
    updateFilteredPivots: function() {
      return (this.filteredPivots = this.costCentrePivots.filter(pivot => {
        if (
          this.filter.is_provider !== undefined &&
          pivot.as_provider !== this.filter.is_provider
        ) {
          return false;
        }
        if (
          this.filter.is_consumer !== undefined &&
          pivot.as_consumer !== this.filter.is_consumer
        ) {
          return false;
        }
        if (
          this.filter.text !== "" &&
          !pivot.name.toLowerCase().includes(this.filter.text.toLowerCase())
        ) {
          return false;
        }
        return true;
      }));
    },
    cycleTrueFalseUndefined: function(value) {
      switch (value) {
        case undefined:
          return true;
        case true:
          return false;
        case false:
          return undefined;
      }
    },
    resetFilter: function() {
      this.filter.text = "";
      this.filter.is_provider = undefined;
      this.filter.is_consumer = undefined;
      this.updateFilteredPivots();
    },
    setSelected: function() {
      this.selectedPivots.forEach(pivot => {
        pivot.as_provider = this.massSet.as_provider ?? pivot.as_provider;
        pivot.as_consumer = this.massSet.as_consumer ?? pivot.as_consumer;

        if (this.massSet.payment_on_account !== "") {
          pivot.payment_on_account = this.massSet.payment_on_account;
        }
      });
    }
  },
  watch: {
    costCentrePivots: function() {
      this.resetFilter();
    }
  }
};
</script>

<style scoped>
.pivot-table td:nth-child(1) {
  width: 5%;
  padding-left: 25px;
}
.pivot-table td:nth-child(2) {
  width: 49%;
}
.pivot-table td:nth-child(3) {
  width: 12%;
}
.pivot-table td:nth-child(4) {
  width: 12%;
}
.pivot-table td:nth-child(5) {
  width: 15%;
}
.pivot-table td:nth-child(6) {
  width: 5%;
}

.pivot-table .v-input--selection-controls {
  margin-top: 0;
}
.pivot-table .v-input {
  margin-top: 0;
}

.pivot-table th div:nth-child(1) {
  height: 20px;
}
.pivot-table th div:nth-child(2) {
  height: 35px;
  font-weight: normal;
}
</style>
